import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { SearchState } from 'src/app/interfaces/search.interface';
import {
  ConnectionRequestAction,
  DefaultService,
  EmptyResponse,
  PatientDistributionFilter,
  RelationGeography,
} from 'src/app/swagger-api';
import { SearchCategories } from '../../search/search.enum';
import { mapConnection, mapRequestCount } from '../relation-request';
import {
  ConnectionRequest,
  Connections,
  RelationRequests,
  RelationRequestsCount,
  SuggestedUser,
} from '../relation-request.interface';
import { UserRelationRequestService } from './relation-request.service';

@Injectable()
export class ConnectionRequestService extends UserRelationRequestService<ConnectionRequest> {
  private apiService = inject(DefaultService);

  protected fetchTotalCountRequests(): Observable<RelationRequestsCount> {
    return this.apiService.restSocialProfileConnectionsCounters().pipe(map(mapRequestCount));
  }

  protected updateSearchState(state: SearchState): SearchState {
    const { params } = state;

    return {
      ...state,
      params: { queryText: params?.queryText ?? '', itemKind: SearchCategories.USERS },
    };
  }

  protected fetchAcceptedRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
    filtering?: string,
  ): Observable<RelationRequests<ConnectionRequest>> {
    const filters = filtering ? [filtering] : undefined;
    return this.apiService.restMedicalProfileConnectionsIndexVerified(offset, limit, sorting, filters).pipe(
      map((connections: Connections) => ({
        items: connections.items.map(mapConnection),
        totalCount: connections.total_count,
      })),
    );
  }

  protected fetchReceivedRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
    filtering?: string,
  ): Observable<RelationRequests<ConnectionRequest>> {
    const filters = filtering ? [filtering] : undefined;
    return this.apiService.restMedicalProfileConnectionsRequestsIndexReceived(offset, limit, sorting, filters).pipe(
      map((connections: Connections) => ({
        items: connections.items.map(mapConnection),
        totalCount: connections.total_count,
      })),
    );
  }

  protected fetchSentRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
    filtering?: string,
  ): Observable<RelationRequests<ConnectionRequest>> {
    const filters = filtering ? [filtering] : undefined;

    return this.apiService.restMedicalProfileConnectionsRequestsIndex(offset, limit, sorting, filters).pipe(
      map((connections: Connections) => ({
        items: connections.items.map(mapConnection),
        totalCount: connections.total_count,
      })),
    );
  }

  protected fetchRegularUserRelation(
    userId: string,
    offset?: number,
    limit?: number,
    sorting?: string,
  ): Observable<RelationRequests<ConnectionRequest>> {
    return this.apiService.restSocialProfileConnectionsIndexForeignVerified(userId, offset, limit, sorting).pipe(
      map((connections: Connections) => ({
        items: connections.items.map(mapConnection),
        totalCount: connections.total_count,
      })),
    );
  }

  protected fetchRelationGeography(
    _userId: string,
    _filter_by?: PatientDistributionFilter,
    _filter_value?: string,
  ): Observable<RelationGeography> {
    throw new Error('Method not implemented.');
  }

  protected accept({ relatedUser }: ConnectionRequest): Observable<EmptyResponse> {
    return this.apiService.restSocialProfileConnectionsPut(
      { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Verified },
      relatedUser.user_id,
    ) as Observable<EmptyResponse>;
  }

  protected reject({ relatedUser }: ConnectionRequest): Observable<EmptyResponse> {
    return this.apiService.restSocialProfileConnectionsPut(
      { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Rejected },
      relatedUser.user_id,
    ) as Observable<EmptyResponse>;
  }

  protected remove({ relatedUser }: ConnectionRequest): Observable<EmptyResponse> {
    return this.apiService.restSocialProfileConnectionsDelete(relatedUser.user_id) as Observable<EmptyResponse>;
  }

  protected create({ user }: SuggestedUser): Observable<EmptyResponse> {
    return this.apiService.restSocialProfileConnectionsPost({ userId: user.user_id }) as Observable<EmptyResponse>;
  }

  protected removeByUser({ user }: SuggestedUser): Observable<EmptyResponse> {
    return this.apiService.restSocialProfileConnectionsDelete(user.user_id) as Observable<EmptyResponse>;
  }

  protected override acceptByUser({ user }: SuggestedUser): Observable<EmptyResponse> {
    return this.apiService.restSocialProfileConnectionsPut(
      { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Verified },
      user.user_id,
    ) as Observable<EmptyResponse>;
  }
}
